import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiEye, FiShield, FiUsers, FiAward, FiArrowRight } from 'react-icons/fi';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

function About() {
  const values = [
    { icon: <FiEye className="w-6 h-6" />, title: 'Transparency', desc: 'We believe in clear communication and honest pricing', color: 'from-primary-500 to-primary-600' },
    { icon: <FiAward className="w-6 h-6" />, title: 'Quality', desc: 'Every lawyer on our platform is verified and experienced', color: 'from-teal-500 to-teal-600' },
    { icon: <FiUsers className="w-6 h-6" />, title: 'Accessibility', desc: 'Legal help should be available to everyone, anytime', color: 'from-accent-500 to-accent-600' },
    { icon: <FiShield className="w-6 h-6" />, title: 'Security', desc: 'Your privacy and data protection are our top priority', color: 'from-primary-400 to-primary-500' },
  ];

  const stats = [
    { value: '10,000+', label: 'Satisfied Clients' },
    { value: '500+', label: 'Expert Lawyers' },
    { value: '50+', label: 'Practice Areas' },
    { value: '98%', label: 'Satisfaction Rate' },
  ];

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Hero */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0f1f35 0%, #1e3a5f 50%, #172e4d 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-80 h-80 rounded-full" style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.4) 0%, transparent 70%)' }} />
        </div>
        <div className="max-w-6xl mx-auto px-4 py-20 md:py-28 text-center relative">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-3xl md:text-5xl font-bold text-white mb-4">About Bihar Lawyer</motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-white/50 text-lg max-w-xl mx-auto">Revolutionizing access to legal expertise</motion.p>
        </div>
      </div>

      {/* Mission */}
      <div className="max-w-4xl mx-auto px-4 py-20">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center">
          <span className="inline-block px-4 py-1.5 rounded-full bg-primary-50 text-primary-600 text-sm font-medium mb-4">Our Mission</span>
          <p className="text-lg md:text-xl text-secondary-600 leading-relaxed">
            At Bihar Lawyer, we believe everyone deserves access to quality legal advice. Our mission is to bridge the gap between clients seeking legal help and experienced lawyers ready to serve them. We're making legal services more accessible, affordable, and convenient than ever before.
          </p>
        </motion.div>
      </div>

      {/* Values */}
      <div className="max-w-6xl mx-auto px-4 pb-20">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-3">Our Values</h2>
          <p className="text-secondary-500">The principles that guide everything we do</p>
        </div>
        <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((v, i) => (
            <motion.div key={i} variants={fadeUp} className="bg-white rounded-2xl shadow-card border border-secondary-100 p-6 text-center hover:shadow-card-hover transition-shadow">
              <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${v.color} flex items-center justify-center text-white mx-auto mb-4`}>{v.icon}</div>
              <h3 className="font-semibold text-secondary-900 mb-2">{v.title}</h3>
              <p className="text-sm text-secondary-500">{v.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Stats */}
      <div className="relative overflow-hidden py-16" style={{ background: 'linear-gradient(135deg, #0f1f35 0%, #1e3a5f 100%)' }}>
        <div className="max-w-6xl mx-auto px-4">
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((s, i) => (
              <motion.div key={i} variants={fadeUp} className="text-center">
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-1">{s.value}</h3>
                <p className="text-white/50 text-sm">{s.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <h2 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-4">Join Our Community Today</h2>
          <p className="text-secondary-500 mb-8 max-w-lg mx-auto">Whether you need legal help or want to offer your expertise, Bihar Lawyer is the platform for you</p>
          <Link to="/signup" className="btn-primary py-3.5 px-8 text-base gap-2">
            Get Started <FiArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </div>
  );
}

export default About;
